import React from 'react';
import { ChevronDown, Activity } from 'lucide-react';
import { TRACKING_LABELS, TRACKING_KEYS } from '../../lib/tracking';

const UTM_FIELDS = ['utmSource', 'utmMedium', 'utmCampaign', 'utmTerm', 'utmContent', 'gclid', 'fbclid'];
const URL_FIELDS = new Set(['landingUrl', 'referrer']);
const LONG_TEXT_FIELDS = new Set(['userAgent', 'landingUrl', 'referrer']);

/**
 * Expandable "פרטי גלישה ומקור" panel rendered inside each admin lead card.
 * Shows zero state when the lead carries no tracking data; otherwise renders
 * a toggle + full <dl> of populated fields.
 */
const TrackingPanel = ({ lead, isOpen, onToggle }) => {
  const presentKeys = TRACKING_KEYS.filter((k) => lead[k]);

  if (presentKeys.length === 0) {
    return (
      <p className="mt-4 pt-4 border-t border-navy/10 text-[11px] text-navy/40 flex items-center gap-1.5">
        <Activity className="w-3 h-3" aria-hidden="true" />
        אין נתוני גלישה לליד זה (נשלח מטופס ישן או לפני הטמעת המעקב).
      </p>
    );
  }

  const utmCount = UTM_FIELDS.filter((k) => lead[k]).length;
  const panelId = `tracking-${lead.id}`;

  return (
    <div className="mt-4 pt-4 border-t border-navy/10">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 text-xs text-navy/70 hover:text-navy transition-colors"
        aria-expanded={isOpen}
        aria-controls={panelId}
        data-testid={`admin-tracking-toggle-${lead.id}`}
      >
        <span className="flex items-center gap-2">
          <Activity className="w-3.5 h-3.5" aria-hidden="true" />
          <span className="font-medium">פרטי גלישה ומקור</span>
          <span className="text-navy/45">
            ({presentKeys.length} שדות{utmCount > 0 ? ` · ${utmCount} UTM` : ''})
          </span>
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div
          id={panelId}
          className="mt-3 bg-navy/5 border border-navy/10 rounded-sm"
          data-testid={`admin-tracking-panel-${lead.id}`}
        >
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            {TRACKING_KEYS.map((k) => {
              const val = lead[k];
              if (!val) return null;
              const isUrl = URL_FIELDS.has(k);
              const isLong = LONG_TEXT_FIELDS.has(k);
              return (
                <div
                  key={k}
                  className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 px-4 py-2.5 border-b border-navy/5 last:border-b-0"
                >
                  <dt className="text-[11px] uppercase tracking-wider text-navy/45 md:w-36 flex-shrink-0 font-medium">
                    {TRACKING_LABELS[k]}
                  </dt>
                  <dd
                    className={`text-xs text-navy/85 ${isLong ? 'break-all' : ''} font-mono`}
                    style={{ direction: isUrl ? 'ltr' : undefined, textAlign: isUrl ? 'left' : undefined }}
                  >
                    {isUrl ? (
                      <a
                        href={val}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-bronze hover:underline"
                      >
                        {val}
                      </a>
                    ) : (
                      val
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      )}
    </div>
  );
};

export default TrackingPanel;
